import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatNaira } from '@/lib/utils';
import { CaretLeft, CaretRight, Camera, MapPin, Clock, CurrencyCircleDollar, Sparkle, Check } from '@phosphor-icons/react';

const CATS = [
  { id: 'plumbing', name: 'Plumbing' }, { id: 'electrical', name: 'Electrical' },
  { id: 'ac_cooling', name: 'AC & Cooling' }, { id: 'carpentry', name: 'Carpentry' },
  { id: 'painting', name: 'Painting' }, { id: 'tiling', name: 'Tiling' },
  { id: 'welding', name: 'Welding' }, { id: 'solar_power', name: 'Solar & Power' },
  { id: 'auto_services', name: 'Auto Services' }, { id: 'cleaning', name: 'Cleaning' },
  { id: 'interior', name: 'Interior' }, { id: 'masonry', name: 'Masonry' },
];

const STATES = ['Lagos', 'Abuja (FCT)', 'Rivers', 'Kano', 'Ogun', 'Oyo'];

export default function PostJobPage() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [jobId, setJobId] = useState('');

  const [form, setForm] = useState({
    category: params.get('category') || '',
    sub_service: params.get('sub') || '',
    title: '', description: '',
    state: user?.state || '', lga: user?.lga || '',
    pool: 'nearby', days_needed: 1,
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get('/categories').then(r => setCategories(r.data.categories || [])).catch(() => {});
  }, [api]);

  useEffect(() => {
    if (form.state) {
      api.get('/locations').then(r => setLgas(r.data.locations[form.state] || [])).catch(() => {});
    }
  }, [form.state, api]);

  useEffect(() => {
    if (form.category && form.description.length > 20) {
      api.get('/estimate', { params: { category: form.category, desc_length: form.description.length, days: form.days_needed, lga: form.lga, pool: form.pool } })
        .then(r => setEstimate(r.data.estimate)).catch(() => {});
    }
  }, [form.category, form.description, form.days_needed, form.lga, form.pool, api]);

  const totalSteps = 5;
  const canNext = () => {
    if (step === 1) return !!form.category;
    if (step === 2) return form.title.length > 3 && form.description.length >= 20;
    if (step === 3) return !!form.state && !!form.lga;
    if (step === 4) return form.days_needed >= 1;
    return true;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/jobs', form);
      setJobId(data.job.id);
      setSuccess(true);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to post job');
    } finally { setLoading(false); }
  };

  const next = () => {
    if (step === totalSteps) { handleSubmit(); return; }
    setStep(s => s + 1);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] flex items-center justify-center p-6" data-testid="job-success">
        <div className="text-center">
          <div className="w-24 h-24 bg-[#2D6A4F]/10 border-2 border-[#2D6A4F] rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <Check size={40} weight="bold" className="text-[#2D6A4F]" />
          </div>
          <h2 className="font-heading text-2xl font-black text-[#1C1C1C] mb-2">Job Posted!</h2>
          <p className="text-[#5C5C5C] mb-6 font-body">Verified artisans will start bidding soon.</p>
          <p className="text-xs text-[#5C5C5C] mb-6 font-mono bg-[#E8DDD2] px-4 py-2 rounded-lg inline-block">ID: {jobId?.slice(0, 8)}</p>
          <div className="space-y-3">
            <button onClick={() => navigate('/my-jobs')} className="w-full bg-[#1A4D2E] text-white font-bold py-3 rounded-xl font-body" data-testid="view-my-jobs-button">View My Jobs</button>
            <button onClick={() => navigate('/')} className="w-full bg-[#E8DDD2] text-[#5C5C5C] font-bold py-3 rounded-xl font-body">Back to Home</button>
          </div>
        </div>
      </div>
    );
  }

  const cat = categories.find(c => c.id === form.category);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] flex flex-col max-w-[480px] mx-auto" data-testid="post-job-page">
      <div className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5 px-4 py-3">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => step > 1 ? setStep(s => s - 1) : navigate(-1)} className="w-9 h-9 bg-[#E8DDD2] rounded-full flex items-center justify-center" data-testid="post-job-back"><CaretLeft size={18} /></button>
          <span className="font-heading text-lg font-bold text-[#1C1C1C] flex-1">Post a Job</span>
          <span className="text-xs font-bold text-[#5C5C5C] bg-[#E8DDD2] px-3 py-1 rounded-full">Step {step}/{totalSteps}</span>
        </div>
        <div className="w-full bg-[#E5E3DB] h-1 rounded-full"><div className="bg-[#1A4D2E] h-1 rounded-full transition-all" style={{ width: `${(step / totalSteps) * 100}%` }} /></div>
      </div>

      <div className="flex-1 p-5">
        {step === 1 && (
          <div data-testid="step-category">
            <h2 className="font-heading text-xl font-bold text-[#1C1C1C] mb-1">What kind of job?</h2>
            <p className="text-sm text-[#5C5C5C] mb-5 font-body">Pick a category, then choose the service.</p>
            <div className="grid grid-cols-3 gap-2">
              {CATS.map(c => (
                <button key={c.id} data-testid={`cat-${c.id}`} onClick={() => set('category', c.id)} className={`p-3 rounded-xl border text-center text-xs font-semibold transition-all ${form.category === c.id ? 'border-[#E86A33] bg-[#E86A33]/5 text-[#E86A33]' : 'border-[#D4C5B3] bg-white text-[#1C1C1C]'}`}>
                  {c.name}
                </button>
              ))}
            </div>
            {form.category && cat && (
              <div className="mt-4 space-y-2">
                <p className="text-xs font-bold uppercase tracking-wider text-[#5C5C5C] mb-2">Choose Service</p>
                {cat.subs?.map(sub => (
                  <button key={sub} data-testid={`sub-${sub.replace(/\s/g, '-')}`} onClick={() => set('sub_service', sub)} className={`w-full p-3 rounded-lg border text-left text-sm font-body ${form.sub_service === sub ? 'border-[#E86A33] bg-[#E86A33]/5' : 'border-[#D4C5B3] bg-white'}`}>
                    {sub}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div data-testid="step-description">
            <h2 className="font-heading text-xl font-bold text-[#1C1C1C] mb-1">Describe your job</h2>
            <p className="text-sm text-[#5C5C5C] mb-5 font-body">Be specific — helps artisans give better quotes.</p>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2 block">Job Title</label>
            <input data-testid="job-title-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Fix leaking kitchen pipe" className="w-full bg-white border border-[#D4C5B3] rounded-lg px-4 py-3 text-[#1C1C1C] focus:ring-2 focus:ring-[#1A4D2E]/20 outline-none font-body mb-4" />
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2 block">Description</label>
            <textarea data-testid="job-desc-input" value={form.description} onChange={e => set('description', e.target.value)} placeholder="Describe what's wrong, size of space, materials available..." rows={5} className="w-full bg-white border border-[#D4C5B3] rounded-lg px-4 py-3 text-[#1C1C1C] focus:ring-2 focus:ring-[#1A4D2E]/20 outline-none font-body resize-none" />
            <p className={`text-xs mt-1 ${form.description.length >= 20 ? 'text-[#2D6A4F]' : 'text-[#5C5C5C]'}`}>{form.description.length} / 20 minimum</p>
          </div>
        )}

        {step === 3 && (
          <div data-testid="step-location">
            <h2 className="font-heading text-xl font-bold text-[#1C1C1C] mb-1">Where is the job?</h2>
            <p className="text-sm text-[#5C5C5C] mb-5 font-body">Address not shared until you accept a bid.</p>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2 block">State</label>
            <select data-testid="job-state" value={form.state} onChange={e => { set('state', e.target.value); set('lga', ''); }} className="w-full bg-white border border-[#D4C5B3] rounded-lg px-4 py-3 text-[#1C1C1C] outline-none font-body mb-4">
              <option value="">Select State</option>
              {STATES.map(s => <option key={s}>{s}</option>)}
            </select>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2 block">LGA</label>
            <select data-testid="job-lga" value={form.lga} onChange={e => set('lga', e.target.value)} className="w-full bg-white border border-[#D4C5B3] rounded-lg px-4 py-3 text-[#1C1C1C] outline-none font-body">
              <option value="">Select LGA</option>
              {lgas.map(l => <option key={l}>{l}</option>)}
            </select>
          </div>
        )}

        {step === 4 && (
          <div data-testid="step-timing">
            <h2 className="font-heading text-xl font-bold text-[#1C1C1C] mb-1">Timeline & Reach</h2>
            <p className="text-sm text-[#5C5C5C] mb-5 font-body">How many days do you need, and how far should we search?</p>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2 block">Days Needed</label>
            <div className="flex items-center bg-white border border-[#D4C5B3] rounded-lg overflow-hidden mb-4">
              <button onClick={() => set('days_needed', Math.max(1, form.days_needed - 1))} className="w-14 h-14 flex items-center justify-center text-2xl text-[#5C5C5C]">−</button>
              <div className="flex-1 text-center font-heading text-2xl font-bold text-[#1C1C1C]" data-testid="days-value">{form.days_needed}</div>
              <button onClick={() => set('days_needed', Math.min(30, form.days_needed + 1))} className="w-14 h-14 flex items-center justify-center text-2xl text-[#5C5C5C]">+</button>
            </div>
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2 block">Search Pool</label>
            <div className="grid grid-cols-2 gap-3">
              {[{ id: 'nearby', name: 'Nearby', desc: 'Within 20km', rate: '8%' }, { id: 'statewide', name: 'Statewide', desc: 'Same state', rate: '12%' }].map(p => (
                <button key={p.id} data-testid={`pool-${p.id}`} onClick={() => set('pool', p.id)} className={`p-4 rounded-xl border text-center transition-all ${form.pool === p.id ? 'border-[#E86A33] bg-[#E86A33]/5' : 'border-[#D4C5B3] bg-white'}`}>
                  <div className="font-bold text-sm text-[#1C1C1C] font-body">{p.name}</div>
                  <div className="text-xs text-[#5C5C5C] font-body">{p.desc}</div>
                  <span className={`text-xs font-bold mt-2 inline-block px-2 py-0.5 rounded-full ${p.id === 'nearby' ? 'bg-[#2D6A4F]/10 text-[#2D6A4F]' : 'bg-[#E86A33]/10 text-[#E86A33]'}`}>{p.rate} commission</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 5 && (
          <div data-testid="step-review">
            <h2 className="font-heading text-xl font-bold text-[#1C1C1C] mb-1">Review & Post</h2>
            <p className="text-sm text-[#5C5C5C] mb-5 font-body">Check everything looks good.</p>
            <div className="bg-white border border-[#D4C5B3] rounded-xl overflow-hidden mb-4">
              <div className="bg-[#E8DDD2] px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#5C5C5C]">Job Summary</div>
              {[['Category', CATS.find(c => c.id === form.category)?.name || form.category], ['Service', form.sub_service || 'General'], ['Title', form.title], ['Location', `${form.lga}, ${form.state}`], ['Days', form.days_needed], ['Pool', form.pool]].map(([k, v]) => (
                <div key={k} className="flex justify-between px-4 py-3 border-b border-[#D4C5B3] last:border-0 text-sm font-body">
                  <span className="text-[#5C5C5C]">{k}</span><span className="font-semibold text-[#1C1C1C]">{v}</span>
                </div>
              ))}
            </div>
            {estimate && (
              <div className="bg-white border border-[#D4C5B3] rounded-xl overflow-hidden mb-4" data-testid="ai-estimate">
                <div className="bg-gradient-to-r from-[#1A4D2E] to-[#2D6A4F] px-4 py-3 flex items-center gap-2"><Sparkle size={16} className="text-[#F4A261]" /><span className="text-white font-bold text-sm font-body">AI Cost Estimate</span></div>
                {[['Labour', estimate.labour_min, estimate.labour_max], ['Materials', estimate.mats_min, estimate.mats_max], ['Transport', estimate.tfare_min, estimate.tfare_max]].map(([name, min, max]) => (
                  <div key={name} className="flex justify-between px-4 py-3 border-b border-[#D4C5B3] text-sm font-body">
                    <span className="text-[#5C5C5C]">{name}</span><span className="font-bold text-[#E86A33]">{formatNaira(min)} – {formatNaira(max)}</span>
                  </div>
                ))}
                <div className="flex justify-between px-4 py-3 bg-[#E86A33]/5 font-body">
                  <span className="text-[#5C5C5C] font-bold">Total Range</span>
                  <span className="font-heading font-bold text-lg text-[#E86A33]">{formatNaira(estimate.total_min)} – {formatNaira(estimate.total_max)}</span>
                </div>
              </div>
            )}
            <div className="bg-[#F4A261]/10 border border-[#F4A261]/30 rounded-xl p-4 text-sm">
              <span className="font-bold text-[#E86A33]">Booking Fee: {formatNaira(3500)}</span>
              <p className="text-xs text-[#5C5C5C] mt-1 font-body">Non-refundable fee to post your job and start receiving bids.</p>
            </div>
          </div>
        )}
      </div>

      <div className="sticky bottom-0 bg-white/80 backdrop-blur-xl border-t border-black/5 p-4">
        <button data-testid="post-job-next-button" onClick={next} disabled={!canNext() || loading} className="w-full bg-[#1A4D2E] hover:bg-[#133922] text-white font-bold py-3.5 rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2 font-body">
          {loading ? 'Posting...' : step === totalSteps ? 'Post Job' : 'Continue'} {!loading && step < totalSteps && <CaretRight size={16} />}
        </button>
      </div>
    </div>
  );
}
